/**
 * Stops date fields accepting a year longer than four digits.
 *
 * A native <input type="date"> with no `max` lets the year be typed as up to six
 * digits (e.g. 11/22/222222), which then travels to the server as a nonsense date.
 * Browsers cap the year field at the digit count of the input's `max`, so giving
 * every date input a default max of 9999-12-31 limits typing to four digits.
 *
 * Done once, globally, rather than on each of the app's many date fields. Inputs
 * that set their own `max` (e.g. "not in the future") are left alone.
 */
const DEFAULT_MAX = '9999-12-31';

const limit = (input: HTMLInputElement) => {
    if (input.type === 'date' && !input.hasAttribute('max')) input.setAttribute('max', DEFAULT_MAX);
};

const scan = (root: ParentNode) => {
    root.querySelectorAll?.('input[type="date"]').forEach((el) => limit(el as HTMLInputElement));
};

export function limitDateYears(): void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    scan(document);
    new MutationObserver((mutations) => {
        for (const m of mutations) {
            m.addedNodes.forEach((node) => {
                if (node.nodeType !== 1) return;
                const el = node as HTMLElement;
                if (el instanceof HTMLInputElement) limit(el);
                scan(el);
            });
            // A field re-rendered from another type (or without max) into a date input.
            if (m.type === 'attributes' && m.target instanceof HTMLInputElement) limit(m.target);
        }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['type', 'max'] });
}
